import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, CircularProgress, Alert,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Collapse, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api';
import { usePeriodo } from '../contexto/Periodo';
import { useEsMovil, ancho, tablaAncha } from '../estilos/responsivo';
import { pesos } from './Tile';
import { BLOQUE, CONTENEDOR_TABLA } from '../estilos/tarjeta';

const estadoStyle = {
  borrador: { bg: '#fef3c7', color: '#d97706' },
  contabilizado: { bg: '#d1fae5', color: '#059669' },
};

const tipoLabel = {
  factura: 'Factura',
  pago: 'Pago',
  anulacion: 'Anulacion',
  ajuste: 'Ajuste',
  cierre: 'Cierre',
  apertura: 'Apertura',
};

function AsientoRow({ asiento, onContabilizar }) {
  const [open, setOpen] = useState(false);
  const [movimientos, setMovimientos] = useState([]);
  const [loadingMov, setLoadingMov] = useState(false);

  const handleExpand = async () => {
    if (open) { setOpen(false); return; }
    setLoadingMov(true);
    try {
      const res = await api.get(`/contabilidad/asientos/${asiento.id}`);
      setMovimientos(res.data.movimientos || []);
    } catch (err) { console.error(err); }
    finally { setLoadingMov(false); setOpen(true); }
  };

  const es = estadoStyle[asiento.estado] || estadoStyle.borrador;

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, px: 2,
        borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
        '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s',
      }} onClick={handleExpand}>
        <IconButton size="small" sx={{ p: 0.25 }}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#1e293b', minWidth: 50 }}>
          #{asiento.numero}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b', minWidth: 80 }}>{asiento.fecha}</Typography>
        <Chip label={tipoLabel[asiento.tipo] || asiento.tipo} size="small" variant="outlined"
          sx={{ height: 20, fontSize: 10, fontWeight: 500 }} />
        <Typography sx={{ flex: 1, fontSize: 13, color: '#334155' }}>{asiento.descripcion}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', minWidth: 90, textAlign: 'right' }}>
          {pesos((asiento.total_debito || 0))}
        </Typography>
        <Chip label={asiento.estado} size="small" sx={{
          height: 20, fontSize: 10, fontWeight: 600, bgcolor: es.bg, color: es.color,
        }} />
        {asiento.estado === 'borrador' && (
          <Tooltip title="Contabilizar">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onContabilizar(asiento.id); }}
              sx={{ color: '#059669' }}>
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 4, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          {loadingMov ? <CircularProgress size={20} /> : (
            <Table size="small" sx={tablaAncha(620)}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#64748b' }}>Codigo</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#64748b' }}>Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 11, color: '#64748b' }}>Descripcion</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11, color: '#64748b' }}>Debito</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11, color: '#64748b' }}>Credito</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimientos.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{m.cuenta_codigo}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{m.cuenta_nombre}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#64748b' }}>{m.descripcion || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: m.debito > 0 ? 600 : 400, color: m.debito > 0 ? '#1e293b' : '#cbd5e1' }}>
                      {m.debito > 0 ? pesos(m.debito) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, fontWeight: m.credito > 0 ? 600 : 400, color: m.credito > 0 ? '#1e293b' : '#cbd5e1' }}>
                      {m.credito > 0 ? pesos(m.credito) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </>
  );
}

export default function LibroDiario() {
  const esMovil = useEsMovil();
  const { periodo, esActual, enLetras: periodoLargo } = usePeriodo();
  const [asientos, setAsientos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [filtros, setFiltros] = useState({ tipo: '', estado: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({ descripcion: '', tipo: 'ajuste', periodo_fiscal: '', movimientos: [{ cuenta_id: '', debito: 0, credito: 0, descripcion: '' }, { cuenta_id: '', debito: 0, credito: 0, descripcion: '' }] });

  const fetchAsientos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // El período lo manda el menú, no un filtro de esta pantalla.
      if (periodo) params.append('periodo', periodo);
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.estado) params.append('estado', filtros.estado);
      const res = await api.get(`/contabilidad/asientos?${params}`);
      setAsientos(res.data.asientos || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCuentas = async () => {
    try {
      const res = await api.get('/contabilidad/cuentas');
      setCuentas(res.data.filter(c => c.nivel >= 3 && c.activa));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAsientos(); }, []);

  const handleContabilizar = async (id) => {
    try {
      await api.post(`/contabilidad/asientos/${id}/contabilizar`);
      setMsg({ type: 'success', text: 'Asiento contabilizado' });
      fetchAsientos();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Error al contabilizar' });
    }
  };

  const handleOpenCreate = () => {
    fetchCuentas();
    const now = new Date();
    setForm({
      descripcion: '', tipo: 'ajuste',
      periodo_fiscal: periodo,
      movimientos: [
        { cuenta_id: '', debito: 0, credito: 0, descripcion: '' },
        { cuenta_id: '', debito: 0, credito: 0, descripcion: '' },
      ],
    });
    setDialogOpen(true);
  };

  const handleAddMov = () => {
    setForm({ ...form, movimientos: [...form.movimientos, { cuenta_id: '', debito: 0, credito: 0, descripcion: '' }] });
  };

  const handleRemoveMov = (idx) => {
    if (form.movimientos.length <= 2) return;
    setForm({ ...form, movimientos: form.movimientos.filter((_, i) => i !== idx) });
  };

  const handleMovChange = (idx, field, value) => {
    const movs = [...form.movimientos];
    movs[idx] = { ...movs[idx], [field]: value };
    setForm({ ...form, movimientos: movs });
  };

  const totalDebito = form.movimientos.reduce((s, m) => s + (parseFloat(m.debito) || 0), 0);
  const totalCredito = form.movimientos.reduce((s, m) => s + (parseFloat(m.credito) || 0), 0);
  const balanced = Math.abs(totalDebito - totalCredito) < 0.01 && totalDebito > 0;

  const handleSave = async () => {
    try {
      await api.post('/contabilidad/asientos', {
        descripcion: form.descripcion,
        tipo: form.tipo,
        periodo_fiscal: form.periodo_fiscal,
        movimientos: form.movimientos.map(m => ({
          cuenta_id: parseInt(m.cuenta_id),
          debito: parseFloat(m.debito) || 0,
          credito: parseFloat(m.credito) || 0,
          descripcion: m.descripcion || null,
        })),
      });
      setMsg({ type: 'success', text: 'Asiento creado en borrador' });
      setDialogOpen(false);
      fetchAsientos();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Error al crear asiento' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>{total} asientos contables</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchAsientos}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}
            sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
            Nuevo Asiento
          </Button>
        </Box>
      </Box>

      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2, borderRadius: 1 }}>{msg.text}</Alert>}

      {/* Filtros */}
      <Paper elevation={0} sx={{ ...BLOQUE, mb: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField label="Tipo" size="small" value={filtros.tipo} onChange={e => setFiltros({ ...filtros, tipo: e.target.value })}
          select sx={ancho(140)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(tipoLabel).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField label="Estado" size="small" value={filtros.estado} onChange={e => setFiltros({ ...filtros, estado: e.target.value })}
          select sx={ancho(140)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="borrador">Borrador</MenuItem>
          <MenuItem value="contabilizado">Contabilizado</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={fetchAsientos}
          sx={{ textTransform: 'none', borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, fontWeight: 600 }}>
          Filtrar
        </Button>
      </Paper>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box> : (
        <Paper elevation={0} sx={CONTENEDOR_TABLA}>
          {asientos.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <MenuBookIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
              <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>No hay asientos</Typography>
              <Typography sx={{ color: '#64748b', fontSize: 13 }}>Los asientos se crean automaticamente al emitir facturas y registrar pagos</Typography>
            </Box>
          ) : (
            asientos.map(a => (
              <AsientoRow key={a.id} asiento={a} onContabilizar={handleContabilizar} />
            ))
          )}
        </Paper>
      )}

      {/* Dialog crear asiento manual */}
      <Dialog fullScreen={esMovil} open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>Nuevo Asiento Manual</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField label="Descripcion" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })}
              size="small" sx={{ flex: 1, minWidth: 200 }} />
            <TextField label="Tipo" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
              size="small" select sx={ancho(140)}>
              <MenuItem value="ajuste">Ajuste</MenuItem>
              <MenuItem value="cierre">Cierre</MenuItem>
              <MenuItem value="apertura">Apertura</MenuItem>
            </TextField>
            <TextField label="Periodo" value={form.periodo_fiscal}
              helperText={esActual ? 'Del menú' : `⚠ ${periodoLargo} no es el mes actual`}
              FormHelperTextProps={{ sx: { color: esActual ? undefined : '#b45309' } }}
              onChange={e => setForm({ ...form, periodo_fiscal: e.target.value })}
              size="small" placeholder="2026-02" sx={ancho(130)} />
          </Box>

          <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b', mb: 1 }}>Movimientos</Typography>
          <Table size="small" sx={tablaAncha(620)}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Cuenta</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Descripcion</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11 }}>Debito</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 11 }}>Credito</TableCell>
                <TableCell width={40}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.movimientos.map((m, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField value={m.cuenta_id} onChange={e => handleMovChange(i, 'cuenta_id', e.target.value)}
                      size="small" select fullWidth sx={{ minWidth: 200 }}>
                      {cuentas.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField value={m.descripcion} onChange={e => handleMovChange(i, 'descripcion', e.target.value)}
                      size="small" fullWidth />
                  </TableCell>
                  <TableCell>
                    <TextField value={m.debito} onChange={e => handleMovChange(i, 'debito', e.target.value)}
                      size="small" type="number" sx={ancho(110)} />
                  </TableCell>
                  <TableCell>
                    <TextField value={m.credito} onChange={e => handleMovChange(i, 'credito', e.target.value)}
                      size="small" type="number" sx={ancho(110)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleRemoveMov(i)} disabled={form.movimientos.length <= 2}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={handleAddMov}
              sx={{ textTransform: 'none', color: '#1e3a5f' }}>
              Agregar linea
            </Button>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Debito: <span style={{ color: '#2563eb' }}>{pesos(totalDebito)}</span>
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Credito: <span style={{ color: '#059669' }}>{pesos(totalCredito)}</span>
              </Typography>
              <Chip label={balanced ? 'Balanceado' : 'Desbalanceado'} size="small" sx={{
                fontWeight: 600, fontSize: 10,
                bgcolor: balanced ? '#d1fae5' : '#fee2e2',
                color: balanced ? '#059669' : '#dc2626',
              }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!balanced || !form.descripcion || !form.periodo_fiscal}
            sx={{ bgcolor: '#1e3a5f', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: '#2d5a87' } }}>
            Crear Asiento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
